import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import axios from 'axios';
import { Search, Clock, Store, AlertTriangle, ListX, RefreshCw, Music4 } from 'lucide-react';

import CustomAlert from "../components/CustomAlert";
import ConfirmationModal from "../components/ConfirmationModal";
import fetchHistory from "../constants/history";
import { clearActiveRoomId } from '../lib/activeRoomSession';
import removeCredit from "../constants/RemoveCredit";
import getUser from "../constants/getUser";
import { initDynamicFirebase } from "../constants/initDynamicFirebase";
import Footerbar from "../components/Footerbar";
import Navbar from "../components/Navbar";

if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}

const API_BASE_URL = 'https://apem.riderup.uk/api';
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const sortHistory = (items) => [...(Array.isArray(items) ? items : [])]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
const filterHistory = (items, query) => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return items;

    return items.filter(item =>
        item.video_title?.toLowerCase().includes(normalizedQuery) ||
        item.client_name?.toLowerCase().includes(normalizedQuery) ||
        item.party_room_name?.toLowerCase().includes(normalizedQuery)
    );
};

const getCurrentCoordinates = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
        reject(new Error('Este dispositivo não oferece acesso à localização.'));
        return;
    }

    navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
        (positionError) => reject(new Error(positionError.code === 1
            ? 'Permita o acesso à localização para pedir músicas na sala.'
            : 'Não foi possível obter sua localização. Verifique o GPS e tente novamente.')),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
});

const HistoryScreen = () => {
    const [history, setHistory] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [reorderingId, setReorderingId] = useState(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: "", type: "error" });

    const [confirmShow, setConfirmShow] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [itemToReorder, setItemToReorder] = useState(null);

    const [oferecimento, setOferecimento] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const loadSponsor = async () => {
            try {
                const user = await getUser();
                if (!user?.client_id) {
                    if (isMounted) setOferecimento(null);
                    return;
                }

                const sponsorResponse = await axios.get(`${API_BASE_URL}/oferecimentos/active/${user.client_id}`);
                if (isMounted) {
                    setOferecimento(sponsorResponse?.data?.data || null);
                }
            } catch (sponsorError) {
                if (isMounted) {
                    setOferecimento(null);
                }
            }
        };

        loadSponsor();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        const getHistory = async () => {
            setLoading(true);
            setError(null);
            setHistory([]);
            setFilteredHistory([]);
            try {
                const data = await fetchHistory();
                if (isMounted) {
                    const sortedData = sortHistory(data);
                    setHistory(sortedData);
                    setFilteredHistory(sortedData);
                }
            } catch (err) {
                if (isMounted) setError(err.message || "Não foi possível carregar o histórico.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        getHistory();
        return () => { isMounted = false; };
    }, []);

    const handleSearch = (text) => {
        setSearchQuery(text);
        setFilteredHistory(filterHistory(history, text));
    };

    const handleReorder = (item) => {
        if (reorderingId) return;

        setItemToReorder(item);
        setConfirmMessage(`Pedir novamente a música "${item.video_title || 'esta música'}"?`);
        setConfirmShow(true);
    };

    const executeReorder = async () => {
        const item = itemToReorder;
        if (!item) return;

        const activeRoomId = localStorage.getItem('activeRoomId');

        setConfirmShow(false);
        setConfirmMessage("");
        setItemToReorder(null);

        setReorderingId(item.id);
        setError(null);

        try {
            if (activeRoomId) {
                const coordinates = await getCurrentCoordinates();
                await axios.post(`${API_BASE_URL}/rooms/${activeRoomId}/requests`, {
                    video_id: item.video_id,
                    ...coordinates,
                }, { headers: getAuthHeaders() });
                const updatedHistory = sortHistory(await fetchHistory());
                setHistory(updatedHistory);
                setFilteredHistory(filterHistory(updatedHistory, searchQuery));
                setAlertInfo({ show: true, message: 'Música enviada para a fila da sala!', type: 'success' });
                return;
            }

            if (item.source === 'party_room') {
                throw new Error(`Conecte-se à sala ${item.party_room_name} para pedir essa música novamente.`);
            }

            const user = await getUser();
            if (!user) throw new Error("Não foi possível obter informações do usuário.");

            if (!user.client) throw new Error("Você precisa estar conectado a um estabelecimento.");

            const config = {
                apiKey: "AIzaSyBMVTvCciOYjyTFES0wapwkiVxTRL_9WJU",
                authDomain: "apemall.firebaseapp.com",
                projectId: "apemall",
                storageBucket: "apemall.firebasestorage.app",
                messagingSenderId: "824620532506",
                appId: "1:824620532506:web:1378444a5c52e09807e2a3",
                measurementId: "G-1YDCLNVCKH"
            };
            if (!config.apiKey || !config.projectId) throw new Error("Configuração do Firebase do bar atual está incompleta.");

            const firebaseDb = await initDynamicFirebase(config);

            const creditResponse = await removeCredit(item.video_id);
            if (!creditResponse?.success) throw new Error(creditResponse?.error || "Erro ao debitar crédito.");

            const empresaId = String(user.client_id);
            const videosRef = collection(firebaseDb, 'empresa', empresaId, 'lista');
            const q = query(videosRef, where("id", "==", item.video_id));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) throw new Error("Esta música já está na fila deste bar.");

            const docToAdd = {
                author: user.name ? user.name.toUpperCase() : 'USUÁRIO APP',
                date: '',
                user_id: user.id,
                fecha: Date.now(),
                id: item.video_id,
                id_ponto: user.client?.id || 1,
                img: item.video_thumbnail || `https://i.ytimg.com/vi/${item.video_id}/mqdefault.jpg`,
                title: item.video_title || 'Título Desconhecido',
            };

            await addDoc(videosRef, docToAdd);
            setAlertInfo({ show: true, message: "Música adicionada com sucesso à fila do bar atual!", type: 'success' });
        } catch (e) {
            if (activeRoomId && [403, 404, 409, 410].includes(e.response?.status)) {
                clearActiveRoomId();
            }
            const responseData = e.response?.data;
            const validationMessage = Object.values(responseData?.errors || {}).flat()[0];
            const genericValidation = e.response?.status === 422
                && (!responseData?.message || responseData.message === 'The given data was invalid.');
            const message = validationMessage || (genericValidation
                ? 'A sala recusou esse pedido. Confira os limites de músicas, duração e regras da sala.'
                : responseData?.message || responseData?.error || e.message);
            setAlertInfo({ show: true, message: `Erro: ${message || "Não foi possível pedir a música."}`, type: 'error' });
        } finally {
            if (document.getElementById('history-screen-root')) {
                setReorderingId(null);
            }
        }
    };

    const cancelReorder = () => {
        setConfirmShow(false);
        setConfirmMessage("");
        setItemToReorder(null);
    };

    const handleCloseAlert = () => setAlertInfo({ ...alertInfo, show: false });

    const formatDate = (dateString) => {
        if (!dateString) return 'Data indisponível';
        try {
            const date = new Date(dateString);
            return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
        } catch (e) {
            return 'Data inválida';
        }
    };

    return (
        <div id="history-screen-root" style={styles.pageContainer}>
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    type={alertInfo.type}
                    onclose={handleCloseAlert}
                />
            )}

            <ConfirmationModal
                show={confirmShow}
                message={confirmMessage}
                onConfirm={executeReorder}
                onCancel={cancelReorder}
            />

            {reorderingId && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingCard}>
                        <div style={styles.loadingTag}>Oferecimento</div>
                        <h2 style={styles.loadingTitle}>
                            {oferecimento?.titulo || 'Esta música é um oferecimento'}
                        </h2>
                        <p style={styles.loadingSubtitle}>
                            {oferecimento?.subtitulo || 'Aguarde enquanto seu pedido está sendo enviado.'}
                        </p>

                        <div style={styles.sponsorImageWrap}>
                            {oferecimento?.imagem ? (
                                <img
                                    src={oferecimento.imagem}
                                    alt={oferecimento?.titulo || 'Oferecimento'}
                                    style={styles.sponsorImage}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <div style={styles.sponsorPlaceholder}>
                                    <Music4 size={48} color="#ffffff" />
                                </div>
                            )}
                        </div>

                        <div style={styles.loadingRow}>
                            <div style={styles.spinner}></div>
                            <span style={styles.loadingText}>Adicionando sua música...</span>
                        </div>
                    </div>
                </div>
            )}

            <Navbar title="Histórico de Pedidos" showLogo={false} />

            <div style={styles.contentArea}>
                <div style={styles.searchContainer}>
                    <Search
                        size={20}
                        color={styles.searchInputIcon.color}
                        style={styles.searchInputIcon}
                    />
                    <input
                        type="text"
                        style={styles.searchInput}
                        placeholder="Buscar por música, estabelecimento ou sala..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>

                {loading && (
                    <div style={styles.centeredMessage}>
                        <div style={styles.spinner}></div>
                        <p>Carregando histórico...</p>
                    </div>
                )}

                {error && !loading && (
                    <div style={styles.centeredMessage}>
                        <AlertTriangle size={40} color="#ff6060" />
                        <p style={styles.errorText}>{error}</p>
                    </div>
                )}

                {!loading && !error && filteredHistory.length === 0 && (
                    <div style={styles.centeredMessage}>
                        <ListX size={40} color="#a0a0b0" />
                        <p style={{ color: "#a0a0b0", marginTop: '10px' }}>
                            {searchQuery ? 'Nenhum resultado encontrado.' : 'Seu histórico de pedidos está vazio.'}
                        </p>
                    </div>
                )}

                {!loading && !error && filteredHistory.length > 0 && (
                    <div style={styles.listContainer}>
                        {filteredHistory.map((item) => (
                            <div key={item.id} style={styles.historyCard}>
                                <img
                                    src={item.video_thumbnail || `https://i.ytimg.com/vi/${item.video_id}/mqdefault.jpg`}
                                    alt="Thumbnail"
                                    style={styles.thumbnail}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />

                                <div style={styles.itemInfo}>
                                    <h3 style={styles.itemTitle}>{item.video_title}</h3>

                                    {(item.source === 'party_room' ? item.party_room_name || item.client_name : item.client_name) && (
                                        <p style={styles.itemClient}>
                                            {item.source === 'party_room'
                                                ? <Music4 size={12} style={{ marginRight: '4px', opacity: 0.8 }} />
                                                : <Store size={12} style={{ marginRight: '4px', opacity: 0.8 }} />}
                                            {item.source === 'party_room'
                                                ? `Sala: ${item.party_room_name || item.client_name?.replace(/^Sala:\s*/, '') || 'Sala encerrada'}`
                                                : item.client_name}
                                        </p>
                                    )}

                                    {item.source === 'party_room' && item.requester_name && (
                                        <p style={styles.itemClient}>Pedido feito por {item.requester_name}</p>
                                    )}

                                    <p style={styles.itemDate}>
                                        <Clock size={12} style={{ marginRight: '4px', opacity: 0.8 }} />
                                        {formatDate(item.created_at)}
                                    </p>

                                    <button
                                        type="button"
                                        style={styles.reorderButton(reorderingId === item.id)}
                                        disabled={!!reorderingId}
                                        onClick={() => handleReorder(item)}
                                    >
                                        {reorderingId === item.id ? (
                                            <>
                                                <div style={styles.buttonSpinner}></div> Pedindo...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={14} /> Pedir Novamente
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Footerbar />
        </div>
    );
};

const styles = {
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#12121f',
        color: '#f0f0f0',
        overflowX: 'hidden',
        boxSizing: 'border-box'
    },
    contentArea: {
        flexGrow: 1,
        paddingTop: '70px',
        paddingBottom: '75px',
        paddingLeft: '10px',
        paddingRight: '10px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        width: '100%'
    },
    searchContainer: {
        position: 'relative',
        marginBottom: '20px',
        width: '100%',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    searchInput: {
        width: "100%",
        backgroundColor: "#1a1a2e",
        padding: "12px 15px 12px 40px",
        borderRadius: "8px",
        fontSize: "14px",
        border: "1px solid #4a4a6e",
        color: '#f0f0f0',
        outline: 'none',
        boxSizing: 'border-box'
    },
    searchInputIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#a0a0b0',
        pointerEvents: 'none'
    },
    centeredMessage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 20px',
        color: '#a0a0b0',
        minHeight: 'calc(80vh - 145px)',
        boxSizing: 'border-box'
    },
    errorText: {
        color: '#ff6060',
        marginTop: '10px',
        maxWidth: '80%',
        lineHeight: '1.4',
        overflowWrap: 'break-word',
        wordWrap: 'break-word'
    },
    spinner: {
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#a0a0ff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        animation: 'spin 1s linear infinite',
        marginBottom: '15px'
    },
    listContainer: {
        width: '100%',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    historyCard: {
        display: "flex",
        alignItems: "flex-start",
        backgroundColor: "#1a1a2e",
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "12px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
        border: '1px solid #2a2a4e',
        boxSizing: 'border-box'
    },
    thumbnail: {
        width: "60px",
        height: "60px",
        borderRadius: "6px",
        marginRight: "12px",
        objectFit: "cover",
        flexShrink: 0,
        backgroundColor: '#333'
    },
    itemInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
    },
    itemTitle: {
        fontSize: "14px",
        fontWeight: "600",
        color: "#ffffff",
        marginBottom: "4px",
        lineHeight: '1.3',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    itemClient: {
        fontSize: "12px",
        color: "#a0a0b0",
        fontWeight: '500',
        marginBottom: "4px",
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    itemDate: {
        fontSize: "11px",
        color: "#777",
        marginBottom: "8px",
        display: 'flex',
        alignItems: 'center'
    },
    reorderButton: (isLoading) => ({
        backgroundColor: isLoading ? '#555' : '#3a3a5e',
        color: isLoading ? '#aaa' : '#c0c0ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: "8px 10px",
        alignSelf: 'flex-start',
        borderRadius: "5px",
        fontSize: "12px",
        fontWeight: "bold",
        border: `1px solid ${isLoading ? '#666' : '#5a5a8e'}`,
        cursor: isLoading ? "not-allowed" : "pointer",
        transition: 'background-color 0.3s ease, color 0.3s ease, opacity 0.3s ease',
        opacity: isLoading ? 0.7 : 1,
        boxSizing: 'border-box',
        minHeight: '32px'
    }),
    buttonSpinner: {
        border: '3px solid rgba(255, 255, 255, 0.2)',
        borderLeftColor: '#c0c0ff',
        borderRadius: '50%',
        width: '12px',
        height: '12px',
        animation: 'spin 1s linear infinite',
        display: 'inline-block'
    },
    loadingOverlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(8, 8, 16, 0.92)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
    },
    loadingCard: {
        width: '100%',
        maxWidth: '420px',
        background: 'linear-gradient(180deg, #19192a 0%, #11111d 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '24px',
        padding: '28px 22px',
        boxShadow: '0 25px 70px rgba(0,0,0,0.45)',
        textAlign: 'center'
    },
    loadingTag: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 12px',
        borderRadius: '999px',
        backgroundColor: 'rgba(192, 192, 255, 0.12)',
        border: '1px solid rgba(192, 192, 255, 0.25)',
        color: '#d8d8ff',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: '14px'
    },
    loadingTitle: {
        margin: '0 0 8px 0',
        fontSize: '24px',
        lineHeight: 1.2,
        color: '#ffffff',
        fontWeight: 800
    },
    loadingSubtitle: {
        margin: '0 0 20px 0',
        fontSize: '14px',
        lineHeight: 1.5,
        color: '#b8b8c8'
    },
    sponsorImageWrap: {
        width: '100%',
        maxWidth: '320px',
        height: '320px',
        margin: '0 auto 20px auto',
        borderRadius: '20px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #202038 0%, #171729 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    sponsorImage: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block'
    },
    sponsorPlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #5a5a8e 0%, #2f2f55 100%)'
    },
    loadingRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
    },
    loadingText: {
        color: '#e9e9f4',
        fontSize: '15px',
        fontWeight: 600
    },
};

export default HistoryScreen;
